import { useMemo, useState } from 'react';

import { Download, Plus, UserCheck } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import { IrregularidadeDaDocumentacao } from '@/modulos/operacao/cadastros/IrregularidadeDaDocumentacao';
import { SEM_PERMISSOES } from '@/modulos/operacao/cadastros/leitura';
import { UltimaSincronizacaoCell } from '@/modulos/operacao/comum/UltimaSincronizacaoCell';
import { OPCOES_DE_SITUACAO_DA_PESSOA, situacaoComoSelo } from '@/modulos/operacao/comum/contrato';
import { useRecursosAtivosEmOs } from '@/modulos/operacao/comum/dados';
import { STATUS_TONE } from '@/nucleo/statusBadgeTokens';
import { useUrlFilters } from '@/nucleo/ganchos/useUrlFilters';
import { OPCOES_DE_VENCIMENTO, type PresetDeVencimento } from '@/nucleo/vencimento';
import { CopyableCpfCnpj } from '@/ui/copyable';
import { DataTable, type DataTableColumn } from '@/ui/DataTable';
import { ErrorState } from '@/ui/ErrorState';
import { FilterBar, type FilterFieldConfig } from '@/ui/FilterBar';
import { FilterPresets, type FilterPreset } from '@/ui/FilterPresets';
import { PageHeader } from '@/ui/PageHeader';
import { ResponsiveModal } from '@/ui/ResponsiveModal';
import { StatusBadge } from '@/ui/StatusBadge';
import { Badge } from '@/ui/base/badge';
import { Button } from '@/ui/base/button';
import { useTableViewPreferences } from '@/ui/table-view/useTableViewPreferences';
import type { TableViewColumn } from '@/ui/table-view/tabelaVisoes';

import { CondutorFormulario } from './CondutorFormulario';
import { useBaixarCondutoresCsv, useCondutores } from './dados';
import type { Condutor, FiltroDeCondutores } from './contrato';

/**
 * `/operacao/condutores` — quem dirige no transporte escolar.
 *
 * DUAS COISAS QUE ESTA TELA UNIFICOU, e valem registro porque parecem ausencia:
 *
 * 1. PREPOSTO NAO TEM TELA PROPRIA. No protótipo havia `/prepostos`, sobre a mesma
 *    tabela. Duas telas para a mesma linha significam dois lugares para editar e uma
 *    divergencia garantida. Aqui preposto e um selo na coluna de nome e um filtro.
 * 2. NAO HA COLUNA DE DIRETORIA. O condutor nao se liga a uma DRE diretamente: ele se
 *    liga ao credenciado, e e o credenciado que tem diretoria. Repetir a DRE aqui
 *    obrigaria a tela a escolher uma quando o condutor atende dois credenciados de
 *    diretorias diferentes — e a escolha seria arbitraria.
 *
 * "NOVO CONDUTOR" ABRE O FORMULARIO, MAS O "SALVAR" NAO GRAVA NADA AINDA. Nenhuma
 * tabela cadastral aceita escrita hoje — nem de `ADMIN` (docs/ALVOS.md, Onda 2) —, e
 * nao ha rota de criacao na API. O dono do repositorio decidiu, em 2026-08-20, que a
 * interface pode ficar pronta antes do backend: o botao e o formulario existem, e o
 * "Salvar" mostra um aviso honesto em vez de fingir sucesso
 * (`comum/cadastroIndisponivel.ts`).
 *
 * LE DA API (`GET bff/v1/condutores`), sob o recorte do banco: a diretoria ve so os
 * condutores de credenciado da propria DRE (0012). O CPF chega inteiro, so digitos —
 * nas areas logadas nao ha mascara (decisao do dono comunicada por Rafael em 2026-10-05) —,
 * e a tela formata. O que o perfil
 * le alem do cadastro vem em `permissoes`: sem leitura da documentacao da J-05, a coluna
 * de irregularidade e o filtro de CRMC nao aparecem; sem leitura de OS, o "Ativo em OS"
 * tambem nao (regra 7 de interface).
 *
 * O CLIQUE NA LINHA VAI PARA `/operacao/condutores/:slug` (`CadastroDetalhePage`).
 */
export function CondutoresPage() {
  const navigate = useNavigate();

  const padroes = useMemo(
    () => ({ search: '', status: '', preposto: '', crmc: '', ativoOs: '' }),
    [],
  );
  const { values, setFilter, setMany, clearAll, page, setPage, perPage, setPerPage } =
    useUrlFilters(padroes);

  const filtro = useMemo<FiltroDeCondutores>(
    () => ({
      pagina: page,
      tamanho: perPage,
      busca: values.search || undefined,
      situacao: values.status || undefined,
      // `undefined` e nao `false`: "não filtrar por preposto" e diferente de "só quem
      // não é preposto", e mandar `false` traria a segunda coisa.
      ehPreposto: values.preposto === 'sim' ? true : undefined,
      crmcVencimento: (values.crmc || undefined) as PresetDeVencimento | undefined,
      ativoEmOs: values.ativoOs === 'sim' ? true : undefined,
    }),
    [page, perPage, values.search, values.status, values.preposto, values.crmc, values.ativoOs],
  );

  const { data, isLoading, isError, error, refetch } = useCondutores(filtro);
  const permissoes = data?.permissoes ?? SEM_PERMISSOES;

  /**
   * "Ativo em OS": o filtro roda no servidor (a paginacao fica certa), e o selo "Em OS"
   * da linha vem de `GET /ordens-de-servico/recursos-ativos` — OS da jornada ATIVA e
   * vigente hoje. So para quem le OS.
   */
  const { data: recursosAtivos } = useRecursosAtivosEmOs(permissoes.ordensDeServico);

  const linhas = useMemo(() => data?.itens ?? [], [data?.itens]);

  const temFiltro = Boolean(
    values.search || values.status || values.preposto || values.crmc || values.ativoOs,
  );

  const campos: FilterFieldConfig[] = [
    {
      key: 'search',
      label: 'Busca',
      type: 'search',
      placeholder: 'Buscar por nome ou CPF…',
      ariaLabel: 'Buscar condutores',
      flex: true,
    },
    ...(permissoes.documentacao
      ? [
          {
            key: 'crmc',
            label: 'CRMC',
            type: 'select' as const,
            options: [...OPCOES_DE_VENCIMENTO],
            placeholder: 'Vencimento do CRMC',
            width: 'w-full sm:w-[180px]',
          },
        ]
      : []),
    {
      key: 'preposto',
      label: 'Preposto',
      type: 'select',
      options: [{ value: 'sim', label: 'Apenas prepostos' }],
      width: 'w-full sm:w-[180px]',
    },
    ...(permissoes.ordensDeServico
      ? [
          {
            key: 'ativoOs',
            label: 'Ativo em OS',
            type: 'select' as const,
            options: [{ value: 'sim', label: 'Apenas ativos em OS' }],
            width: 'w-full sm:w-[180px]',
          },
        ]
      : []),
    {
      key: 'status',
      label: 'Situação',
      type: 'select',
      options: [...OPCOES_DE_SITUACAO_DA_PESSOA],
      width: 'w-full sm:w-[150px]',
    },
  ];

  /**
   * Atalhos: as perguntas que a equipe faz todo dia.
   *
   * "CRMC vencido" e o motivo pelo qual alguem abre esta tela — deixar isso atras de dois
   * seletores transforma a rotina em quatro cliques e uma chance de errar o campo.
   */
  const atalhosDaDocumentacao: FilterPreset[] = [
    {
      id: 'crmc-vencido',
      label: 'CRMC vencido',
      values: { crmc: 'vencido' },
      description: 'Condutores com o Cadastro Municipal de Condutor já vencido',
    },
    {
      id: 'crmc-30',
      label: 'CRMC vence em 30 dias',
      values: { crmc: '30dias' },
      description: 'Condutores com CRMC vencendo nos próximos 30 dias',
    },
  ];
  const atalhos: FilterPreset[] = [
    ...(permissoes.documentacao ? atalhosDaDocumentacao : []),
    ...(permissoes.ordensDeServico
      ? [{ id: 'ativos-os', label: 'Ativos em OS', values: { ativoOs: 'sim' } }]
      : []),
    { id: 'ativos', label: 'Ativos', values: { status: 'ATIVO' } },
  ];

  const colunas = useMemo<TableViewColumn<Condutor>[]>(
    () => [
      {
        id: 'nome',
        header: 'Nome',
        cell: (condutor) => (
          <div className="flex items-center gap-2">
            <span className="font-medium">{condutor.nome}</span>
            {recursosAtivos?.condutores.has(condutor.id) && (
              <Badge variant="outline" className="h-5 px-1.5 text-[10px]">
                Em OS
              </Badge>
            )}
            {condutor.eh_preposto && (
              <span
                className={`rounded px-1.5 py-0.5 text-[10px] uppercase tracking-wider ${STATUS_TONE.info}`}
              >
                Preposto
              </span>
            )}
          </div>
        ),
        sortKey: 'nome',
        isCritical: true,
        canHide: false,
        linkedFilterKeys: ['preposto'],
      },
      {
        id: 'cpf',
        header: 'CPF',
        // Formatado na tela; o botao copia so os digitos.
        cell: (condutor) =>
          condutor.cpf ? (
            <CopyableCpfCnpj value={condutor.cpf} className="whitespace-nowrap" />
          ) : (
            '—'
          ),
      },
      {
        id: 'crmc',
        header: 'CRMC',
        // So o numero: a validade que vale e a aprovada na J-05, e ela entra na coluna de
        // irregularidade e no filtro de vencimento, nao na coluna `crmc_validade` da 0002.
        cell: (condutor) => <span className="text-sm">{condutor.crmc_numero || '—'}</span>,
        linkedFilterKeys: ['crmc'],
        tooltip:
          'Cadastro Municipal de Condutor — a autorização de São Paulo para transporte escolar. Vale junto com a CNH, não no lugar dela.',
      },
      {
        id: 'credenciado',
        header: 'Credenciado',
        cell: (condutor) => <span className="text-sm">{condutor.credenciados?.nome ?? '—'}</span>,
        sortKey: (condutor) => condutor.credenciados?.nome ?? '',
      },
      ...(permissoes.documentacao
        ? [
            {
              id: 'irregularidade',
              header: 'Irregularidade',
              cell: (condutor: Condutor) => (
                <IrregularidadeDaDocumentacao
                  validades={condutor.validades ?? []}
                  situacao={condutor.status}
                  semVinculo={!condutor.credenciado_id}
                />
              ),
              tooltip: 'Validade aprovada de cada documento em vigor, na análise do DTP.',
            },
          ]
        : []),
      {
        id: 'ultima_sincronizacao',
        header: 'Última sincronização',
        cell: (condutor) => (
          <UltimaSincronizacaoCell
            origem={condutor.origem_dado}
            sincronizadoEm={condutor.sincronizado_em}
            emContingencia={condutor.em_contingencia}
          />
        ),
        sortKey: 'sincronizado_em',
      },
      {
        id: 'status',
        header: 'Situação',
        cell: (condutor) => (
          <StatusBadge status={situacaoComoSelo(condutor.status)} label={condutor.status} />
        ),
        sortKey: 'status',
        linkedFilterKeys: ['status'],
        isCritical: true,
        canHide: false,
        isStatusColumn: true,
      },
    ],
    [permissoes.documentacao, recursosAtivos],
  );

  const tableView = useTableViewPreferences({
    contextKey: 'condutores:list',
    columns: colunas,
    filterValues: values,
  });

  const baixarCsv = useBaixarCondutoresCsv();
  const [baixando, setBaixando] = useState(false);
  const [novoAberto, setNovoAberto] = useState(false);

  const exportar = async () => {
    setBaixando(true);
    try {
      const quantidade = await baixarCsv(filtro);
      toast('Planilha gerada', {
        description: `${quantidade} condutor(es) no arquivo, com os filtros que você está usando.`,
      });
    } catch {
      toast.error('Não foi possível gerar o arquivo', {
        description: 'Tente novamente em instantes.',
      });
    } finally {
      setBaixando(false);
    }
  };

  return (
    <div className="space-y-4">
      <PageHeader
        title="Condutores"
        description="Quem dirige no transporte escolar, com a habilitação e os cursos em dia."
      >
        <Button
          variant="outline"
          onClick={() => void exportar()}
          disabled={baixando || isLoading}
          aria-label="Exportar condutores em CSV"
        >
          <Download className="h-4 w-4 sm:mr-2" aria-hidden="true" />
          <span className="hidden sm:inline">{baixando ? 'Gerando…' : 'Exportar CSV'}</span>
        </Button>
        <Button onClick={() => setNovoAberto(true)}>
          <Plus className="h-4 w-4 sm:mr-2" aria-hidden="true" />
          <span className="hidden sm:inline">Novo Condutor</span>
        </Button>
      </PageHeader>

      <ResponsiveModal
        open={novoAberto}
        onOpenChange={setNovoAberto}
        title={
          <>
            <UserCheck className="h-5 w-5 text-primary" aria-hidden="true" />
            Novo Condutor
          </>
        }
        description="Cadastre um novo condutor no programa TEG."
        desktopMaxWidth="max-w-4xl"
      >
        <CondutorFormulario onCancelar={() => setNovoAberto(false)} />
      </ResponsiveModal>

      <FilterPresets
        presets={atalhos}
        currentValues={values}
        onApply={(valores) => {
          // Substitui em vez de acumular: atalho e um recorte inteiro, e somar ao filtro
          // anterior produziria combinacoes que ninguem pediu (e as vezes vazias).
          clearAll();
          setMany(valores);
        }}
      />

      <FilterBar
        fields={campos}
        values={values}
        onChange={setFilter}
        onClear={clearAll}
        defaultValues={padroes}
      />

      {isError ? (
        <ErrorState
          variant="load-failed"
          description={
            error?.aviso ??
            'Não foi possível buscar os condutores agora. Verifique sua conexão e tente novamente.'
          }
          primaryAction={{ label: 'Tentar de novo', onClick: () => void refetch() }}
        />
      ) : (
        <DataTable
          columns={tableView.orderedColumns as DataTableColumn<Condutor>[]}
          personalizacao={tableView}
          data={linhas}
          onRowClick={(condutor) => navigate(`/operacao/condutores/${condutor.slug}`)}
          isLoading={isLoading}
          total={data?.total}
          page={page}
          perPage={perPage}
          onPageChange={setPage}
          onPerPageChange={setPerPage}
          emptyIcon={UserCheck}
          emptyTitle="Nenhum condutor encontrado"
          emptyDescription={
            temFiltro
              ? 'Nenhum condutor atende a esses filtros. Tente afrouxar a busca.'
              : 'Quando houver condutor habilitado, ele aparece aqui.'
          }
          minWidth="700px"
          ariaLabel="Lista de condutores"
        />
      )}
    </div>
  );
}

export default CondutoresPage;
